import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { secureRpc } from "@/lib/secureRpc";
import { supabase } from "@/integrations/supabase/client";
import { useFPLBootstrap } from "@/hooks/useFPLData";

export const NOTE_TITLE_LIMIT = 60;
export const NOTE_BODY_LIMIT = 1500;
export const NOTE_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

export type NotePriority = "low" | "normal" | "key";
export type NoteChip = "none" | "wildcard" | "freehit" | "bboost" | "3xc";

export interface ManagerNote {
  id: string;
  gameweek: number;
  title: string | null;
  body: string;
  chip_plan: NoteChip | null;
  captain: string | null;
  transfers_in: string | null;
  transfers_out: string | null;
  priority: NotePriority;
  image_url: string | null;
  reminder: boolean;
  created_at: string;
  updated_at: string;
}

export interface NoteInput {
  gameweek: number;
  title?: string | null;
  body?: string;
  chip_plan?: NoteChip | null;
  captain?: string | null;
  transfers_in?: string | null;
  transfers_out?: string | null;
  priority?: NotePriority;
  image_url?: string | null;
  reminder?: boolean;
}

export interface GameweekMeta {
  id: number;
  name: string;
  deadline_time: string | null;
  finished: boolean;
  is_current: boolean;
  is_next: boolean;
}

export function useManagerNotes() {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { data: bootstrap } = useFPLBootstrap();

  const [notes, setNotes] = useState<ManagerNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gameweeks: GameweekMeta[] = useMemo(() => {
    const raw = (bootstrap as any)?.allGameweeks || (bootstrap as any)?.events || [];
    return (raw as any[])
      .filter(Boolean)
      .map((g) => ({
        id: Number(g.id),
        name: g.name || `Gameweek ${g.id}`,
        deadline_time: g.deadline_time ?? null,
        finished: Boolean(g.finished),
        is_current: Boolean(g.is_current),
        is_next: Boolean(g.is_next),
      }))
      .filter((g) => g.id >= 1 && g.id <= 38);
  }, [bootstrap]);

  const currentGw = useMemo(() => {
    const cur = gameweeks.find((g) => g.is_current);
    if (cur) return cur.id;
    const next = gameweeks.find((g) => g.is_next);
    return next ? next.id : 1;
  }, [gameweeks]);

  const refresh = useCallback(async () => {
    if (!isSignedIn || !user) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await secureRpc<ManagerNote[]>("manager_notes_list", {}, getToken);
    if (err) setError(err.message);
    else setNotes(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [isSignedIn, user, getToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveNote = useCallback(
    async (input: NoteInput) => {
      const { error: err } = await secureRpc<string>(
        "manager_notes_upsert",
        {
          _gameweek: input.gameweek,
          _title: input.title?.slice(0, NOTE_TITLE_LIMIT) ?? null,
          _body: (input.body ?? "").slice(0, NOTE_BODY_LIMIT),
          _chip_plan: input.chip_plan && input.chip_plan !== "none" ? input.chip_plan : null,
          _captain: input.captain ?? null,
          _transfers_in: input.transfers_in ?? null,
          _transfers_out: input.transfers_out ?? null,
          _priority: input.priority ?? "normal",
          _image_url: input.image_url ?? null,
          _reminder: input.reminder ?? false,
        },
        getToken,
      );
      if (err) throw new Error(err.message);
      await refresh();
    },
    [getToken, refresh],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      const { error: err } = await secureRpc<boolean>("manager_notes_delete", { _note_id: noteId }, getToken);
      if (err) throw new Error(err.message);
      await refresh();
    },
    [getToken, refresh],
  );

  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      if (!user) throw new Error("You must be signed in");
      if (file.size > NOTE_IMAGE_MAX_BYTES) throw new Error("Image must be 2MB or smaller");
      if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
        throw new Error("Only JPG, PNG or WebP images are allowed");
      }

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = () => reject(new Error("Could not read the image"));
        reader.readAsDataURL(file);
      });

      const token = await getToken();
      const { data, error: fnErr } = await supabase.functions.invoke("upload-note-image", {
        headers: token
          ? { Authorization: `Bearer ${token}`, "X-Clerk-Authorization": `Bearer ${token}` }
          : {},
        body: { image_base64: base64, image_mime: file.type },
      });
      if (fnErr || !(data as any)?.success) {
        throw new Error((data as any)?.error || fnErr?.message || "Upload failed");
      }
      // Store the private storage path; display goes through a signed link.
      return ((data as any).path ?? (data as any).url) as string;
    },
    [user, getToken],
  );


  return { notes, loading, error, gameweeks, currentGw, refresh, saveNote, deleteNote, uploadImage };
}
